import React from 'react';
import { Badge } from '@dopomogai/ui';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Terminal, Receipt, ArrowRightLeft } from 'lucide-react'; 

export type ProblemStatementSectionProps = {
  titleKey?: string;
  subtitleKey?: string;
  textKey?: string;
  imageSrc?: string;
};

export const ProblemStatementSection: React.FC<ProblemStatementSectionProps> = ({
  imageSrc = '/images/illustrations/human-middleware-sq.avif' 
}) => {
  const { t } = useTranslation('homepage');

  return (
    <section className="bg-slate-50 py-16 md:py-24 overflow-hidden relative border-b-2 border-slate-200">
      
      {/* 1. GRAPH PAPER GRID BACKGROUND (Bureaucracy/Admin Theme) */}
      <div 
        className="absolute inset-0 opacity-[0.035] pointer-events-none" 
        style={{ 
            backgroundImage: `
                linear-gradient(to right, #000 1px, transparent 1px),
                linear-gradient(to bottom, #000 1px, transparent 1px)
            `, 
            backgroundSize: '40px 40px',
            // Fade out edges so it doesn't look like a hard grid
            maskImage: 'radial-gradient(ellipse at center, black 50%, transparent 100%)'
        }} 
      />

      {/* 2. SUBTLE ROSE GLOW (To signal "Crisis") */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-rose-300/10 blur-[120px] rounded-full pointer-events-none -z-0 translate-x-1/3 -translate-y-1/3" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          
          {/* LEFT: The "Human Router" Visual */}
          <div className="relative order-2 lg:order-1 flex justify-center">
             
             <motion.div
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.8 }}
               className="relative w-full max-w-[540px]"
             >
                {/* THE FRAME: Dark container for the dark image */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-slate-900 bg-slate-900">
                    <img 
                      src={imageSrc} 
                      alt="Human acting as middleware" 
                      className="w-full h-auto object-cover opacity-90 hover:opacity-100 transition-opacity duration-700"
                    />
                    {/* Scanline Overlay */}
                    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 pointer-events-none mix-blend-overlay" />
                </div>

                {/* --- FLOATING UI ELEMENTS --- */}
                
                {/* 1. The Cost Label - Using Rose to match section theme */}
                <motion.div 
                  className="absolute top-[20%] -right-[5%] md:-right-[10%] bg-white text-slate-900 border-2 border-rose-500 px-4 py-2.5 rounded-xl text-sm font-black shadow-[6px_6px_0_0_#e11d48] rotate-[2deg] z-20 flex items-center gap-3"
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="bg-rose-100 p-1.5 rounded-lg">
                    <ArrowRightLeft size={18} className="text-rose-600" />
                  </div>
                  <div className="flex flex-col leading-none">
                    <span className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Burn Rate</span>
                    <span className="text-base font-black">$60k/yr Human Middleware</span>
                  </div>
                </motion.div>

                {/* 2. The Status Label - Using Orange to match the cable inside image */}
                 <motion.div 
                  className="absolute bottom-[10%] -left-[2%] md:-left-[5%] bg-slate-900 text-orange-500 border border-slate-700 px-4 py-2 rounded-lg text-xs font-mono shadow-xl flex items-center gap-2 z-30"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                  </span>
                  STATUS: BOTTLENECK
                </motion.div>
             </motion.div>
          </div>

          {/* --- RIGHT: THE COPY --- */}
          <div className="order-1 lg:order-2">
            <div className="mb-6">
               <Badge 
                 text="The Velocity Crisis" 
                 accentColor="rose" 
                 className="border-2 border-rose-200 bg-rose-50 text-rose-800" 
               />
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 leading-[1.0] mb-6 tracking-tight uppercase">
              Stop Paying The<br />
              <span className="text-rose-600">
                Human Router Tax.
              </span>
            </h2>
            
            <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-4">
              You are hiring smart humans to do the work of APIs.
            </h3>
            
            <p className="text-lg text-slate-600 leading-relaxed mb-10 max-w-lg font-medium">
              Your team is drowning in admin: moving data from Slack to Jira, summarizing CRMs, and checking logs. This isn't work; it's friction.
              <br/><br/>
              <span className="text-slate-900 font-bold">The New Physics:</span> You don't need another tool for your people to use. You need a <span className="font-bold text-emerald-600">Digital Workforce</span> to use the tools for you and deliver <span className="font-bold text-emerald-600">10x results</span>.
            </p>

            {/* THE COMPARISON WIDGETS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              
              {/* 1. The "Old Way" Receipt */}
              <div className="bg-white border-2 border-slate-200 rounded-xl p-5 relative shadow-sm transform rotate-[-1deg] hover:rotate-0 transition-transform duration-300">
                <div className="absolute -top-3 -left-3 bg-slate-100 text-slate-500 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded border border-slate-200">
                  Manual Ops
                </div>
                <div className="flex items-center gap-2 mb-4 border-b border-dashed border-slate-300 pb-2">
                  <Receipt size={16} className="text-slate-400" />
                  <span className="text-xs font-mono text-slate-500 uppercase">Monthly Burn</span>
                </div>
                <ul className="space-y-2 text-sm font-mono text-slate-600 mb-4">
                  <li className="flex justify-between">
                    <span>Human Middleware</span>
                    <span className="text-red-500">-$5,000</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Seat Licenses</span>
                    <span className="text-red-500">-$450</span>
                  </li>
                  <li className="flex justify-between text-xs text-slate-400 pt-2 border-t border-dashed border-slate-200">
                    <span>Output:</span>
                    <span>40 Actions/Day</span>
                  </li>
                </ul>
                <div className="border-t-2 border-slate-900 pt-2 flex justify-between font-black text-slate-900">
                  <span>TOTAL</span>
                  <span>$65k/yr</span>
                </div>
              </div>

              {/* 2. The "New Way" Terminal */}
              <div className="bg-slate-900 border-2 border-black rounded-xl p-5 relative shadow-[8px_8px_0_0_#10b981] transform rotate-[1deg] hover:rotate-0 transition-transform duration-300 z-10">
                <div className="absolute -top-3 -right-3 bg-emerald-500 text-white px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded border-2 border-black">
                  Autonomous Ops
                </div>
                <div className="flex items-center gap-2 mb-4 border-b border-slate-700 pb-2">
                  <Terminal size={16} className="text-emerald-400" />
                  <span className="text-xs font-mono text-emerald-400/70 uppercase">Private Server</span>
                </div>
                <div className="space-y-2 text-sm font-mono text-emerald-50 mb-4">
                  <div className="flex justify-between">
                    <span>Server Cost</span>
                    <span className="text-emerald-400">$200</span>
                  </div>
                  <div className="flex justify-between">
                    <span>AI Usage</span>
                    <span className="text-emerald-400">$500</span>
                  </div>
                   <div className="flex justify-between text-xs text-emerald-500/60 pt-2 border-t border-slate-700">
                    <span>Output:</span>
                    <span className="text-emerald-400 font-bold">400 Actions/Day</span>
                  </div>
                </div>
                <div className="border-t border-slate-700 pt-2 flex justify-between items-center">
                  <span className="text-xs text-slate-400 font-mono">HUMAN ROLE</span>
                  <div className="text-right">
                    <span className="block font-bold text-white text-xs bg-emerald-900/50 px-2 py-1 rounded border border-emerald-500/30">Approvals Only</span>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};